import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Composant SEO dynamique pour ATOM Group
 * Optimise le référencement de chaque page
 */
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  type = 'website',
  image,
  article = null,
  localBusiness = null,
  faq = null,
  noindex = false,
  breadcrumb = null,
  service = null
}) => {
  const siteName = "ATOM Group SA";
  const baseUrl = "https://www.atom-group.ch";
  const defaultImage = "https://www.atom-group.ch/assets/logo/atom-logo-white.png";
  
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - Conseil Financier Indépendant Suisse`;
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const imageUrl = image || defaultImage;

  // Données structurées pour les articles/guides
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.headline,
    "description": article.description,
    "image": imageUrl,
    "author": {
      "@type": "Organization",
      "name": "ATOM Group SA"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ATOM Group SA",
      "logo": {
        "@type": "ImageObject",
        "url": defaultImage
      }
    },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "mainEntityOfPage": canonicalUrl,
    "inLanguage": "fr-CH",
    ...(article.about ? {
      "about": article.about.map(topic => ({ "@type": "Thing", "name": topic }))
    } : {}),
    ...(article.audience ? {
      "audience": { "@type": "Audience", "audienceType": article.audience }
    } : {}),
    "isAccessibleForFree": true
  } : null;

  // Données structurées pour les services locaux
  const localBusinessSchema = localBusiness ? {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${baseUrl}/#organization`,
    "name": "ATOM Group SA",
    "image": defaultImage,
    "telephone": "+41325391627",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "c/o Microcity SA, Rue de la Pierre-à-Mazel 39",
      "addressLocality": "Neuchâtel",
      "postalCode": "2000",
      "addressCountry": "CH"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 46.9927,
      "longitude": 6.9393
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": "Suisse",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "bestRating": "5",
      "worstRating": "1",
      "reviewCount": "41"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services financiers",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Conseil financier",
            "description": "Conseil personnalisé en optimisation financière"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "3ème pilier",
            "description": "Optimisation du 3ème pilier pour la retraite"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Assurances",
            "description": "Conseil en assurances vie et maladie complémentaire"
          }
        }
      ]
    }
  } : null;

  // Données structurées pour les FAQ
  const faqSchema = faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Fil d'Ariane : aide les moteurs génératifs à situer la page dans le site
  const breadcrumbSchema = breadcrumb ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { name: "Accueil", path: "/" },
      ...breadcrumb
    ].map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${baseUrl}${item.path}`
    }))
  } : null;

  // Description structurée du service décrit par la page
  const serviceSchema = service ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${canonicalUrl}#service`,
    "name": service.name,
    "description": service.description,
    "serviceType": service.serviceType || service.name,
    "provider": {
      "@type": "Organization",
      "@id": `${baseUrl}/#organization`,
      "name": "ATOM Group SA"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Suisse"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": service.audience || "Particuliers et PME en Suisse"
    },
    ...(service.offers ? {
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": service.name,
        "itemListElement": service.offers.map(offer => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": offer
          }
        }))
      }
    } : {})
  } : null;

  // Schema Organization global (toujours présent)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${baseUrl}/#organization`,
    "name": "ATOM Group SA",
    "url": baseUrl,
    "logo": defaultImage,
    "description": "Conseil financier indépendant en Suisse. Optimisation fiscale, 3ème pilier, assurances, hypothèques.",
    "email": "contact@atom-group.ch",
    "telephone": "+41325391627",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "c/o Microcity SA, Rue de la Pierre-à-Mazel 39",
      "addressLocality": "Neuchâtel",
      "postalCode": "2000",
      "addressCountry": "CH"
    },
    "memberOf": {
      "@type": "Organization",
      "name": "Association des Courtiers en Assurances",
      "alternateName": "ACA",
      "url": "https://aca-courtiers.ch/"
    },
    "sameAs": [
      "https://www.linkedin.com/company/atom-group-sa/",
      "https://www.google.com/maps/place/ATOM+Group+SA"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "bestRating": "5",
      "worstRating": "1",
      "reviewCount": "41"
    }
  };

  return (
    <Helmet>
      {/* Balises meta de base */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large"} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="fr_CH" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Données structurées */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      
      {localBusinessSchema && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
      
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
      
      {/* Organization Schema - toujours présent */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
};

// Configurations SEO prédéfinies pour chaque page
export const SEOPages = {
  home: {
    title: "Conseil Financier Indépendant Suisse - Optimisation Fiscale & 3ème Pilier",
    description: "ATOM Group SA, conseil financier indépendant en Suisse. Optimisation fiscale, 3ème pilier, assurances, hypothèques. Première consultation gratuite. 100% transparent.",
    keywords: "conseil financier suisse, optimisation fiscale, 3ème pilier, assurance suisse, hypothèque suisse, LPP, courtier assurance, prévoyance suisse",
    canonical: "/",
    localBusiness: true
  },
  
  contact: {
    title: "Contact - Demandez votre Analyse Financière Gratuite",
    description: "Contactez ATOM Group SA pour une première analyse financière gratuite. Conseil personnalisé en optimisation fiscale, 3ème pilier et assurances. Neuchâtel, Suisse.",
    keywords: "contact conseil financier, consultation gratuite, analyse financière, Neuchâtel, Suisse",
    canonical: "/contact",
    breadcrumb: [{ name: "Contact", path: "/contact" }],
    localBusiness: true
  },
  
  assurances: {
    title: "Assurances Vie & Maladie Complémentaire Suisse - Conseil Expert",
    description: "Conseil expert en assurances vie, maladie complémentaire et responsabilité civile en Suisse. Comparaison des meilleures offres, optimisation des primes.",
    keywords: "assurance vie suisse, maladie complémentaire, assurance responsabilité civile, primes assurance, conseil assurance suisse",
    canonical: "/assurances",
    localBusiness: true,
    breadcrumb: [{ name: "Assurances", path: "/assurances" }],
    service: {
      name: "Courtage et conseil en assurances",
      description: "Analyse des besoins, comparaison du marché et souscription d'assurances vie, maladie complémentaire, responsabilité civile, ménage et protection juridique en Suisse.",
      serviceType: "Courtage en assurances",
      offers: [
        "Assurance vie",
        "Assurance maladie complémentaire",
        "Assurance responsabilité civile",
        "Assurance ménage",
        "Protection juridique"
      ]
    }
  },

  fiscalite: {
    title: "Optimisation Fiscale Suisse - Réduisez vos Impôts Légalement",
    description: "Optimisation fiscale légale en Suisse : 3ème pilier, déductions fiscales, planification succession. Économisez des milliers de francs d'impôts chaque année.",
    keywords: "optimisation fiscale suisse, réduction impôts, déductions fiscales, 3ème pilier fiscal, planification fiscale",
    canonical: "/fiscalite",
    breadcrumb: [{ name: "Fiscalité", path: "/fiscalite" }],
    service: {
      name: "Optimisation fiscale",
      description: "Identification des déductions applicables, planification des rachats et retraits, et structuration du patrimoine pour réduire légalement la charge fiscale en Suisse.",
      serviceType: "Conseil fiscal",
      offers: [
        "Déductions fiscales 3ème pilier",
        "Planification des rachats LPP",
        "Échelonnement des retraits de prévoyance",
        "Planification successorale"
      ]
    }
  },

  retraite: {
    title: "Prévoyance Retraite & 3ème Pilier Suisse - Planification Expert",
    description: "Planification retraite complète : 3ème pilier A et B, LPP, AVS. Optimisez votre prévoyance pour une retraite confortable en Suisse.",
    keywords: "retraite suisse, 3ème pilier, prévoyance, LPP, AVS, planification retraite, retraite anticipée",
    canonical: "/retraite",
    breadcrumb: [{ name: "Retraite et prévoyance", path: "/retraite" }],
    service: {
      name: "Planification de la retraite et de la prévoyance",
      description: "Analyse des trois piliers, calcul des lacunes de prévoyance et planification du départ à la retraite, y compris anticipé, pour les résidents suisses.",
      serviceType: "Planification de prévoyance",
      offers: [
        "3ème pilier A et B",
        "Rachat LPP",
        "Analyse des lacunes de prévoyance",
        "Retraite anticipée",
        "Arbitrage rente ou capital"
      ]
    }
  },

  investissements: {
    title: "Investissements & Gestion de Patrimoine Suisse - Conseil Indépendant",
    description: "Investissements et gestion de patrimoine en Suisse. Portefeuilles diversifiés, ETF, actions suisses. Conseil indépendant et transparent.",
    keywords: "investissement suisse, gestion patrimoine, ETF suisse, actions suisses, portefeuille investissement, conseil investissement",
    canonical: "/investissements",
    breadcrumb: [{ name: "Investissements", path: "/investissements" }],
    service: {
      name: "Conseil en investissement et gestion de patrimoine",
      description: "Construction de portefeuilles diversifiés adaptés au profil de risque, sélection de supports et suivi dans la durée, sans dépendance à un établissement unique.",
      serviceType: "Gestion de patrimoine",
      offers: [
        "Portefeuilles diversifiés",
        "ETF et fonds indiciels",
        "Plans d'épargne",
        "Analyse de profil de risque"
      ]
    }
  },

  immobilier: {
    title: "Financement Hypothécaire Suisse - Meilleurs Taux & Conseil Expert",
    description: "Financement hypothécaire en Suisse : meilleurs taux, conseil achat immobilier, refinancement. Accompagnement complet pour votre projet immobilier.",
    keywords: "hypothèque suisse, financement immobilier, taux hypothécaire, achat immobilier suisse, refinancement",
    canonical: "/immobilier",
    breadcrumb: [{ name: "Immobilier", path: "/immobilier" }],
    service: {
      name: "Financement hypothécaire",
      description: "Calcul de la capacité d'achat, mise en concurrence des établissements prêteurs, structuration de l'amortissement et refinancement de prêts existants en Suisse.",
      serviceType: "Courtage hypothécaire",
      offers: [
        "Prêt hypothécaire",
        "Refinancement",
        "Calcul de capacité d'achat",
        "Investissement locatif"
      ]
    }
  },

  creditsPrives: {
    title: "Crédit Privé Suisse - Comparaison et Demande en Ligne",
    description: "Crédit privé en Suisse : comparaison des offres de plusieurs établissements et accompagnement du dossier. Réponse rapide, sans frais de dossier.",
    keywords: "crédit privé suisse, prêt personnel, comparaison crédit, taux crédit suisse, demande de crédit en ligne",
    canonical: "/credits-prives",
    breadcrumb: [{ name: "Crédits privés", path: "/credits-prives" }],
    service: {
      name: "Courtage en crédit privé",
      description: "Comparaison des offres de crédit à la consommation de plusieurs établissements suisses et accompagnement du dossier jusqu'au déblocage des fonds.",
      serviceType: "Courtage en crédit",
      offers: [
        "Crédit à la consommation",
        "Regroupement de crédits",
        "Financement de projet"
      ]
    }
  },

  apropos: {
    title: "À Propos - Notre Équipe de Conseillers Financiers Indépendants",
    description: "Cabinet de conseil financier indépendant à Neuchâtel : notre équipe, nos valeurs et nos agréments. Conforme LSFIN, enregistré FINMA, membre de l'ACA.",
    keywords: "ATOM Group SA, conseillers financiers Neuchâtel, cabinet indépendant suisse, équipe conseil financier, FINMA, LSFIN, ACA",
    canonical: "/a-propos",
    localBusiness: true,
    breadcrumb: [{ name: "À propos", path: "/a-propos" }]
  },

  demandeCredit: {
    title: "Demande de Crédit en Ligne - Réponse Rapide",
    description: "Déposez votre demande de crédit privé en ligne auprès d'ATOM Group SA. Formulaire sécurisé, étude de dossier gratuite et sans engagement.",
    keywords: "demande crédit en ligne, formulaire crédit suisse, demande prêt personnel",
    canonical: "/demande-credit",
    breadcrumb: [
      { name: "Crédits privés", path: "/credits-prives" },
      { name: "Demande de crédit", path: "/demande-credit" }
    ]
  },

  conditionsCredit: {
    title: "Conditions et Mentions Légales des Demandes de Crédit",
    description: "Mentions légales applicables aux demandes de crédit déposées auprès d'ATOM Group SA : conditions, traitement des données et information au consommateur.",
    canonical: "/conditions-credit",
    breadcrumb: [
      { name: "Crédits privés", path: "/credits-prives" },
      { name: "Conditions de crédit", path: "/conditions-credit" }
    ]
  },

  mentionsLegales: {
    title: "Mentions Légales",
    description: "Mentions légales d'ATOM Group SA : informations sur l'entreprise, représentants légaux, activité et surveillance, hébergement et conditions d'utilisation du site.",
    canonical: "/mentions-legales",
    breadcrumb: [{ name: "Mentions légales", path: "/mentions-legales" }]
  },

  confidentialite: {
    title: "Politique de Confidentialité et Protection des Données",
    description: "Politique de confidentialité d'ATOM Group SA : données collectées, finalités du traitement, base légale, partage et droits des personnes concernées (nLPD).",
    canonical: "/confidentialite",
    breadcrumb: [{ name: "Confidentialité", path: "/confidentialite" }]
  },

  consentementConjoint: {
    title: "Consentement du Conjoint",
    description: "Page de confirmation du consentement du conjoint dans le cadre d'une demande de crédit.",
    canonical: "/consentement-conjoint",
    noindex: true
  },
  
  mindfinance: {
    title: "ATOM MindFinance - Bien-être Financier en Entreprise | Solution RH Suisse",
    description: "Programme de bien-être financier pour PME suisses : réduisez l'absentéisme, le turnover et le stress de vos employés. Pack Sérénité CHF 250 offert.",
    keywords: "bien-être financier entreprise, stress financier employés, solution RH suisse, absentéisme, turnover, santé financière collaborateurs, MindFinance",
    canonical: "/mindfinance",
    breadcrumb: [{ name: "MindFinance", path: "/mindfinance" }],
    localBusiness: true
  },
  
  pme: {
    title: "Solutions Financières PME Suisse - Prévoyance Professionnelle & Optimisation",
    description: "Solutions financières pour PME suisses : prévoyance professionnelle LPP, optimisation fiscale entreprise, assurances collectives. Conseil expert ATOM Group SA.",
    keywords: "solutions PME suisse, prévoyance professionnelle, LPP entreprise, fiscalité PME, assurances collectives, conseil PME",
    canonical: "/pme",
    breadcrumb: [{ name: "Indépendants & PME", path: "/pme" }],
    localBusiness: true
  },
  
  recrutement: {
    title: "Recrutement ATOM Group - Rejoignez Notre Équipe de Conseillers Financiers",
    description: "Rejoignez ATOM Group SA : conseillers financiers, experts en prévoyance et assurances. Environnement dynamique, formation continue. Postes à Neuchâtel.",
    keywords: "recrutement conseiller financier, emploi assurance suisse, carrière finance Neuchâtel, conseiller prévoyance",
    canonical: "/recrutement",
    breadcrumb: [{ name: "Recrutement", path: "/recrutement" }]
  },
  
  guide3emePilier: {
    title: "Guide Complet du 3ème Pilier - 3a ou 3b, Plafonds et Fiscalité",
    description: "Guide complet du 3ème pilier en Suisse romande : différences entre 3a et 3b, plafonds de déduction, avantages fiscaux et critères de choix d'un produit.",
    keywords: "3ème pilier Suisse romande, 3ème pilier Vaud, 3ème pilier Genève, optimisation 3ème pilier, avantages fiscaux 3ème pilier, 3a vs 3b, prévoyance Suisse",
    canonical: "/guide-3eme-pilier",
    type: "article",
    breadcrumb: [
      { name: "Guides", path: "/guides" },
      { name: "Guide du 3ème pilier", path: "/guide-3eme-pilier" }
    ],
    article: {
      headline: "Guide complet du 3ème pilier en Suisse",
      description: "Différences entre le pilier 3a et 3b, plafonds de déduction annuels, traitement fiscal des versements et des retraits, et critères de choix entre les solutions bancaires et d'assurance.",
      about: ["3ème pilier", "Pilier 3a", "Pilier 3b", "Prévoyance individuelle", "Déduction fiscale"],
      audience: "Résidents suisses souhaitant constituer une prévoyance individuelle"
    }
  },

  guideRachat2emePilier: {
    title: "Guide du Rachat 2ème Pilier - Calcul, Fiscalité et Timing",
    description: "Guide complet du rachat du 2ème pilier en Suisse : calcul du potentiel de rachat, économie d'impôt réalisable, échelonnement et délai de blocage avant retrait.",
    keywords: "rachat 2ème pilier, rachat LPP, optimisation fiscale Suisse, lacune prévoyance, déduction fiscale",
    canonical: "/guide-rachat-2eme-pilier",
    type: "article",
    breadcrumb: [
      { name: "Guides", path: "/guides" },
      { name: "Guide du rachat 2ème pilier", path: "/guide-rachat-2eme-pilier" }
    ],
    article: {
      headline: "Guide du rachat du 2ème pilier en Suisse",
      description: "Comment déterminer son potentiel de rachat LPP, quelle économie d'impôt en attendre, comment échelonner les versements et quelles contraintes de délai respecter avant un retrait.",
      about: ["Rachat LPP", "2ème pilier", "Prévoyance professionnelle", "Optimisation fiscale", "Lacune de prévoyance"],
      audience: "Salariés affiliés à une caisse de pension suisse"
    }
  },

  guideHypotheque: {
    title: "Guide de l'Hypothèque en Suisse - Fonds Propres, Taux et Amortissement",
    description: "Guide complet de l'hypothèque en Suisse : fonds propres exigés, règle des trois tiers, types de taux, amortissement direct ou indirect et utilisation du 3ème pilier.",
    keywords: "hypothèque Suisse romande, hypothèque Vaud, hypothèque Genève, taux hypothécaire, fonds propres, retrait 3ème pilier, amortissement hypothèque",
    canonical: "/guide-hypotheque-suisse",
    type: "article",
    breadcrumb: [
      { name: "Guides", path: "/guides" },
      { name: "Guide de l'hypothèque", path: "/guide-hypotheque-suisse" }
    ],
    article: {
      headline: "Guide de l'hypothèque en Suisse",
      description: "Fonds propres exigés et leur provenance, calcul de la charge théorique, choix entre taux fixe et variable, amortissement direct ou indirect, et recours au 2ème ou 3ème pilier.",
      about: ["Hypothèque", "Financement immobilier", "Fonds propres", "Taux hypothécaire", "Amortissement"],
      audience: "Acquéreurs immobiliers et propriétaires en Suisse"
    }
  },

  guideRetraiteAnticipee: {
    title: "Guide de la Retraite Anticipée en Suisse - Coût, AVS et LPP",
    description: "Guide de la retraite anticipée en Suisse : impact sur l'AVS et la LPP, coût de l'anticipation, financement de la période de pont et impact fiscal.",
    keywords: "retraite anticipée, planification retraite, AVS anticipée, LPP retraite, 3ème pilier retraite",
    canonical: "/guide-planification-retraite-anticipee",
    type: "article",
    breadcrumb: [
      { name: "Guides", path: "/guides" },
      { name: "Guide de la retraite anticipée", path: "/guide-planification-retraite-anticipee" }
    ],
    article: {
      headline: "Guide de la planification d'une retraite anticipée en Suisse",
      description: "Réduction de rente induite par l'anticipation AVS et LPP, calcul du capital nécessaire pour financer la période de pont, et impact fiscal des retraits de prévoyance.",
      about: ["Retraite anticipée", "AVS", "LPP", "Rente de pont", "Planification financière"],
      audience: "Personnes envisageant un départ à la retraite avant l'âge légal en Suisse"
    }
  },

  guidePrevoyanceIndependants: {
    title: "Guide de la Prévoyance des Indépendants en Suisse",
    description: "Guide complet de la prévoyance pour indépendants en Suisse : 3ème pilier renforcé, assurances obligatoires et facultatives, couverture des lacunes propres au statut.",
    keywords: "prévoyance indépendants, 3a indépendants, assurance perte de gain, optimisation fiscale indépendants, prévoyance sans LPP",
    canonical: "/guide-prevoyance-independants",
    type: "article",
    breadcrumb: [
      { name: "Guides", path: "/guides" },
      { name: "Guide de la prévoyance des indépendants", path: "/guide-prevoyance-independants" }
    ],
    article: {
      headline: "Guide de la prévoyance pour les indépendants en Suisse",
      description: "Plafond de déduction majoré du 3a en l'absence de LPP, affiliation facultative à une caisse de pension, assurance perte de gain et couverture des risques décès et invalidité.",
      about: ["Prévoyance des indépendants", "3ème pilier renforcé", "LPP facultative", "Perte de gain", "Optimisation fiscale"],
      audience: "Indépendants et professions libérales en Suisse"
    }
  },

  guideInvestissementLocatif: {
    title: "Guide de l'Investissement Locatif en Suisse - Rendement et Fiscalité",
    description: "Guide complet de l'investissement locatif en Suisse romande : financement, calcul du rendement net, fiscalité du revenu locatif et stratégies d'optimisation.",
    keywords: "investissement locatif Suisse, rendement locatif, fiscalité immobilier, hypothèque investissement, PPE, immeuble de rapport",
    canonical: "/guide-investissement-locatif",
    type: "article",
    breadcrumb: [
      { name: "Guides", path: "/guides" },
      { name: "Guide de l'investissement locatif", path: "/guide-investissement-locatif" }
    ],
    article: {
      headline: "Guide de l'investissement locatif en Suisse",
      description: "Conditions de financement d'un bien de rendement, calcul du rendement brut et net, imposition du revenu locatif et de la valeur locative, et leviers d'optimisation.",
      about: ["Investissement locatif", "Rendement locatif", "Immobilier de rendement", "Fiscalité immobilière", "PPE"],
      audience: "Investisseurs immobiliers en Suisse romande"
    }
  },

  faq: {
    title: "Questions Fréquentes - Conseil Financier & Assurances Suisse",
    description: "Réponses aux questions fréquentes sur le conseil financier, 3ème pilier, assurances et optimisation fiscale en Suisse. Expertise ATOM Group SA.",
    keywords: "FAQ conseil financier, questions assurances suisse, 3ème pilier questions, optimisation fiscale FAQ",
    canonical: "/faq",
    breadcrumb: [{ name: "FAQ", path: "/faq" }]
  },
  
  guides: {
    title: "Guides Financiers Gratuits - 3ème Pilier, Fiscalité, Assurances Suisse",
    description: "Guides financiers gratuits pour optimiser vos finances en Suisse : 3ème pilier, fiscalité, assurances, retraite. Conseils d'experts ATOM Group SA.",
    keywords: "guide financier suisse, guide 3ème pilier, guide fiscal suisse, guide assurance, conseils financiers gratuits",
    canonical: "/guides",
    breadcrumb: [{ name: "Guides", path: "/guides" }]
  }
};

export default SEO;